// The readouts chart's template, chart.readouts (contract §8.3): NeoChart's
// readouts (cloud/web/src/components/NeoChart.tsx), two tiles a row, each a
// reading's name, its value and unit at the small readout size, its age as
// the readouts write it (`data-tc-age-style="readout"`), and a muted trend
// over the chart's own window at 48 points. A tank level in % takes the whole
// row as a LevelMeter, as today. An old reading is grey whatever its value
// (NeoChart's `chart-stale`): a low tank read days ago keeps its "Low", not its
// colour. An angle's trend is its line alone, on its range (§4.5.4). Like
// today's readouts, the card names no window.
//
// The Marina look (cloud ADR 0396, TOM-678): each tile takes its reading's
// kind (`data-domain`) for its trend's line and fading area, and a level for
// its bar; a level past its line keeps its kind's colour and says so with the
// warning mark and its word.

import {
  AREA_CSS,
  LEVEL_PATH,
  angleRange,
  LOW_GOOD_PATH,
  READOUT_CSS,
  areaGradient,
  css,
  domainCss,
  domainOf,
  domainsOf,
  h,
  output,
  pathFacts,
  readingNames,
  reading,
  readout,
  QUARTER_GAPS,
  warnMark,
} from "./kit.js";
import { pageDomainFor } from "./domains.js";
import { chartWindow } from "./chart-trend.js";

const CSS = css(`
  .ro { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 14px; align-content: start; }
  .ro-t { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
  .ro-w { grid-column: 1 / -1; }
  .ro-n { font-size: 13px; font-weight: 500; color: var(--muted); overflow-wrap: break-word; }
  .ro-a { font-size: 12px; color: var(--muted); }
  .ro-sp { display: block; width: 100%; height: 28px; margin-top: 8px; overflow: visible; }
  .ro-sp path:not(.ar) { fill: none; stroke: var(--_c); stroke-width: 2.4; stroke-linecap: round; stroke-linejoin: round; }
  .ro-sp .ar { stroke: none; }
  .ro-sp path:is([d=""], :not([d])) { display: none; }
  .ro-sp [data-quality=missing] { --_c: var(--quiet); }
  .ro-sp .ar[data-quality=missing] { fill: var(--quiet); fill-opacity: 0.12; }
  .ro-lv { display: flex; align-items: center; gap: 12px; width: 100%; --_tone: var(--attention); }
  .ro-lv[data-zone=bad] { --_tone: var(--danger); }
  .ro-lv[data-quality=stale] { --_tone: var(--muted); }
  /* At night a stale value keeps the text colour: grey under the night tint reads below the app's own values (§9.6, TOM-665). */
  html[data-theme=night] .ro-lv[data-quality=stale] { --_tone: var(--text); }
  .ro-lv .ro-n { flex: none; max-width: 40%; font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .ro-tr { position: relative; flex: 1; height: 8px; border-radius: 999px; background: var(--track); min-width: 40px;
    mask-image: ${QUARTER_GAPS}; }
  .ro-f { position: absolute; inset: 0 auto 0 0; border-radius: 999px; background: var(--_c); width: calc(var(--frac,0) * 100%); }
  .ro-f[data-quality=missing] { display: none; }
  .ro-v { flex: none; display: inline-flex; align-items: baseline; gap: 0.18em; font-family: var(--tc-font-rounded);
    font-size: 14px; font-weight: 600; }
  .ro-flag { display: none; align-items: center; gap: 4px; margin-left: 6px; font-size: 12px; font-weight: 600; color: var(--_tone); }
  .ro-wm { width: 14px; height: 14px; flex: none; }
  .ro-lv[data-zone=warn] .ro-flag, .ro-lv[data-zone=bad] .ro-flag { display: inline-flex; }
`);

function levelTile(name, lowGood, domain) {
  return h(
    "div",
    { class: "ro-t ro-w", ...domainOf(domain) },
    h(
      "div",
      { class: "ro-lv", "data-tc-state": name },
      h("span", { class: "ro-n", "data-tc-name": name }),
      h(
        "div",
        { class: "ro-tr" },
        h("div", { class: "ro-f", "data-tc-frac": name }),
      ),
      h(
        "span",
        { class: "ro-v" },
        h("span", { "data-tc-text": name, "data-tc-decimals": "0" }),
        h("span", { class: "rd-u", "data-tc-unit": name }),
        h(
          "span",
          { class: "ro-flag" },
          warnMark("ro-wm"),
          lowGood ? "High" : "Low",
        ),
      ),
    ),
    h("span", {
      class: "ro-a",
      "data-tc-age": name,
      "data-tc-age-style": "readout",
    }),
  );
}

function tile(name, angle, index, domain) {
  const series = { "data-tc-points": "48", "data-tc-inset": "3 1 3 1" };
  return h(
    "div",
    { class: "ro-t", ...domainOf(domain) },
    h("span", { class: "ro-n", "data-tc-name": name }),
    readout(name, 24),
    h("span", {
      class: "ro-a",
      "data-tc-age": name,
      "data-tc-age-style": "readout",
    }),
    h(
      "svg",
      { class: "ro-sp", "data-tc-fit": `t${index}`, "aria-hidden": "true" },
      angle ? "" : h("defs", {}, areaGradient(`ro-a${index}`)),
      angle
        ? ""
        : h("path", {
            class: "ar",
            "data-tc-path": name,
            "data-tc-shape": "area",
            ...series,
            fill: `url(#ro-a${index})`,
          }),
      h("path", { "data-tc-path": name, ...series }),
    ),
  );
}

/** chart.readouts: a readouts chart of 1 to 6 paths (§8.3). */
export function chartReadouts(source, context) {
  const spec = source.spec;
  const paths = (Array.isArray(spec.paths) ? spec.paths : []).slice(0, 6);
  const byPath = readingNames(paths);
  const readings = {};
  const tiles = [];
  for (const [path, name] of byPath) {
    const facts = pathFacts(context, path);
    const level = LEVEL_PATH.test(path) && facts.storeUnit === "%";
    if (level) {
      const lowGood = LOW_GOOD_PATH.test(path);
      readings[name] = reading(
        path,
        [0, 100],
        lowGood
          ? [
              { name: "bad", above: 90 },
              { name: "warn", above: 75 },
            ]
          : [
              { name: "bad", below: 10 },
              { name: "warn", below: 25 },
            ],
      );
      tiles.push(levelTile(name, lowGood, pageDomainFor(path)));
    } else {
      readings[name] = facts.angle
        ? reading(path, angleRange(path, facts.storeUnit))
        : reading(path);
      tiles.push(tile(name, facts.angle, tiles.length, pageDomainFor(path)));
    }
  }
  const window = chartWindow(spec, source.rolling);
  return output("chart.readouts", 1, {
    html: h("div", { class: "tp ro", "data-tc-window": window }, tiles),
    css: READOUT_CSS + CSS + AREA_CSS + domainCss(domainsOf(paths)),
    readings,
    card: { kind: "chart", window: false, chip: null },
  });
}
